import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles, shorthands } from '@fluentui/react-components'
import {
  Document24Filled,
  CheckmarkCircle24Filled,
  Delete24Filled
} from '@fluentui/react-icons'

/**
 * Defines the Fluent UI styles for the AvailableFilesMessage component.
 * Utilizes CSS-in-JS to create scoped class names for the container layout,
 * a scrollable file list with custom scrollbars, text truncation, and specific
 * visual states (like the "cleared" state).
 */
const useStyles = makeStyles({
  container: {
    backgroundColor: '#F5F5F5',
    ...shorthands.borderRadius('6px'),
    ...shorthands.border('1px', 'solid', '#E0E0E0'),
    ...shorthands.padding('6px', '8px'),
    minWidth: '300px',
    maxWidth: '90%',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  header: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
    paddingBottom: '5px',
    marginBottom: '0px',
    ...shorthands.borderBottom('1px', 'solid', '#E0E0E0')
  },
  list: {
    listStyle: 'none',
    ...shorthands.padding(0),
    ...shorthands.margin(0),
    maxHeight: '130px',
    overflowY: 'auto',
    ...shorthands.padding('0', '8px', '0', '0'),

    '&::-webkit-scrollbar': {
      width: '6px'
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#c1c1c1',
      ...shorthands.borderRadius('10px')
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#a8a8a8'
    }
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#605e5c',
    ...shorthands.padding('4px', 0)
  },
  fileIcon: {
    marginRight: '8px',
    color: '#0078d4',
    flexShrink: 0
  },
  fileName: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    color: '#107C10',
    fontSize: '13px',
    fontWeight: '500',
    marginTop: '10px',
    paddingTop: '8px',
    ...shorthands.borderTop('1px', 'solid', '#E0E0E0')
  },
  footerIcon: {
    marginRight: '8px',
    flexShrink: 0
  },
  clearedStateContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#605e5c',
    fontSize: '14px',
    padding: '8px 0'
  },
  clearedStateIcon: {
    marginRight: '8px',
    color: '#D13438'
  }
})

/**
 * A message component displayed in the chat feed to show the status of uploaded files.
 * It has three main states:
 * 1.  Renders a list of available files.
 * 2.  Renders a message indicating all files have been removed (`isCleared`).
 * 3.  Renders nothing (`null`) if there are no files and the `isCleared` flag is not set.
 */
const AvailableFilesMessage = ({ fileNames, showSourceActive, isCleared }) => {
  const styles = useStyles()

  /**
  * Render Block: Cleared State
  * If the 'isCleared' flag is true, bypass the file list and render a single
  * alert message letting the user know all files have been removed.
  */
  if (isCleared) {
    return (
      <div className={styles.container}>
        <div className={styles.clearedStateContainer}>
          <Delete24Filled className={styles.clearedStateIcon} />
          <span>All uploaded files have been removed.</span>
        </div>
      </div>
    )
  }

  /**
  * Guard Clause: Empty State
  * If it's not a "cleared" event and there are no files to display,
  * render nothing at all to prevent showing an empty grey box.
  */
  if (!fileNames || fileNames.length === 0) {
    return null
  }

  /**
  * Default Render: File List
  * Renders a scrollable list of file names along with document icons.
  * Optionally displays a success footer if the source was just set to active.
  */
  return (
    <div className={styles.container}>
      <div className={styles.header}>Available Files</div>
      <ul className={styles.list}>
        {fileNames.map((name, index) =>
          <li key={index} className={styles.listItem}>
            <Document24Filled className={styles.fileIcon} />
            <span className={styles.fileName} title={name}>
              {name}
            </span>
          </li>
        )}
      </ul>
      {showSourceActive &&
        <div className={styles.footer}>
          <CheckmarkCircle24Filled className={styles.footerIcon} />
          <span>The upload source is now active.</span>
        </div>}
    </div>
  )
}


AvailableFilesMessage.propTypes = {
  fileNames: PropTypes.arrayOf(PropTypes.string), 
  showSourceActive: PropTypes.bool,
  isCleared: PropTypes.bool,
}

export default AvailableFilesMessage
